import {AllowedFileExtensionsAiChatUploads} from "../../serverClientShared/sharedEnums";
import { pipeline, env } from '@xenova/transformers';
import TranscribeVideoUtility from "./TranscribeVideoUtility";
import {handleAbort} from "./PdfParser";
env.allowLocalModels = false;
env.useBrowserCache = false;

export default class EmbedFilesUtility {
    static embedderInstance = null;
    static getFileExtension(file) {
        if (file.FileExtension) {
            return file.FileExtension;
        } else {
            const parts = file.name.split('.');
            return parts.length > 1 ? parts.pop().toLowerCase() : '';
        }
    }
    static async embedText (text) {
        try{
            const embedder = await this.getModelConnection('web');
            const vectors = await embedder([text], { pooling: "mean", normalize: true });
            return Array.isArray(vectors[0]) ? Object.values(vectors[0].data) : Object.values(vectors.data);
        } catch (error) {
            console.log(error);
        }
    }
    static async embedFileByType(file, parsedPages, originalFile, onProgress, index, abortedFiles) {
        let parsedChunksArray
        const fileType = this.getFileExtension(file);
        switch (fileType) {
            case AllowedFileExtensionsAiChatUploads.PDF:
            case AllowedFileExtensionsAiChatUploads.DOCX:
            case AllowedFileExtensionsAiChatUploads.TXT:
                parsedChunksArray = await this.createEmbeddingVectors(file, parsedPages, onProgress, index, abortedFiles);
                break;
            case AllowedFileExtensionsAiChatUploads.MP4:
                parsedChunksArray = await this.embedVideo(file, originalFile, abortedFiles);
                break;
            default:
                return null;
        }
        return parsedChunksArray;
    }
    static async embedVideo(file, originalFile, abortedFiles) {
        try {
            if (handleAbort(file, abortedFiles, true)) {
                return [];
            }
            const transcribedChunks = await TranscribeVideoUtility.transcribeVideo(originalFile, abortedFiles);
            if (handleAbort(file, abortedFiles, true)) {
                return [];
            }
            const readyToEmbedChunks = TranscribeVideoUtility.createEmbeddableChunks(transcribedChunks, abortedFiles)
            if (handleAbort(file, abortedFiles, true)) {
                return [];
            }
            const vectors = await this.embedChunksInBatchesTimeStamp(readyToEmbedChunks);
            return vectors
        } catch (error) {
        }
    }
    static async embedChunksInBatchesTimeStamp(embeddableChunks , batchSize = 10) {
        const results = [];
        const embedder = await this.getModelConnection('web');

        for (let i = 0; i < embeddableChunks.length; i += batchSize) {
            const batch = embeddableChunks.slice(i, i + batchSize);
            const textsToEmbed = batch.map(chunk => chunk.textForEmbedding);

            const batchVectors = await embedder(textsToEmbed, {
                pooling: "mean",
                normalize: true
            });


            batch.forEach((chunk, batchIndex) => {
                results.push({
                    pageNumber: i + batchIndex,
                    chunkText: chunk.textWithTimestamps,
                    embedding: Object.values(batchVectors[batchIndex].data)
                });
            });
        }

        return results;
    }
    static async getModelConnection (environment){
        try {
            if (this.embedderInstance){
                return this.embedderInstance;
            }
            let embedderInstance;
            switch (environment) {
                case 'web':
                    embedderInstance = await pipeline(
                        "feature-extraction",
                        "Xenova/all-MiniLM-L6-v2"
                    );
                    break;
                case 'onPrem':
                    const originalURL = env.remoteURL;
                    const originalAllowLocal = env.allowLocalModels;
                    env.allowLocalModels = true;
                    env.remoteURL = 'http://newtesting.localhost:3002/models/';
                    embedderInstance = await pipeline(
                        "feature-extraction",
                        "all-MiniLM-L6-v2"
                    );
                    env.remoteURL = originalURL;
                    env.allowLocalModels = originalAllowLocal;
                    break;
                default:
                    throw new Error(`Unknown environment: ${environment}`);
            }
            this.embedderInstance  = embedderInstance
            return embedderInstance;
        } catch (error) {
            throw error;
        }
    }

    static async createEmbeddingVectors(file, parsedPages, onProgress, index, abortedFiles) {
        try{
            const embedder = await this.getModelConnection('web');

            const batchSize = 10;
            const vectors = [];


            for (let i = 0; i < parsedPages.length; i += batchSize) {
                if (handleAbort({name:''}, abortedFiles, true)) {
                    await new Promise(requestAnimationFrame);
                    return [];
                }
                const batch = parsedPages.slice(i, i + batchSize);
                const chunkedTexts = [];
                const chunkMetadata = [];

                for (let pageIdx = 0; pageIdx < batch.length; pageIdx++) {
                    const raw = batch[pageIdx];
                    const decoded = raw.replace(/\\x3C/g, "<").replace(/\\x3E/g, ">");
                    const cleaned = decoded.replace(/<\/?(h1|h2|p|table|tr|td|th)>/gi, '').trim();

                    const chunks = this.splitIntoChunks(cleaned).filter(text => text && text.length > 0);
                    for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
                        chunkedTexts.push(chunks[chunkIdx]);
                        chunkMetadata.push({
                            pageNumber: i + pageIdx + 1,
                            totalChunks: chunks.length,
                            chunkText: decoded.trim(),
                        });
                    }
                }
                const chunkBatchSize = 25;
                for (let i = 0; i < chunkedTexts.length; i += chunkBatchSize) {
                    if (handleAbort({name:''}, abortedFiles, true)) {
                        return [];
                    }
                    const chunkBatch = chunkedTexts.slice(i, i + chunkBatchSize);
                    const batchVectors = await embedder(chunkBatch, { pooling: "mean", normalize: true });

                    for (let j = 0; j < chunkBatch.length; j++) {
                        vectors.push({
                            embedding: Object.values(batchVectors[j].data),
                            ...chunkMetadata[i + j]
                        });
                    }
                }
                const totalPageBatches = Math.ceil(parsedPages.length / batchSize);
                const currentBatchNumber = Math.floor(i / batchSize) + 1;
                const batchProgress = currentBatchNumber / totalPageBatches;
                const x = 0.5 + batchProgress * 0.25;
                onProgress(x, null, index);
                await new Promise(requestAnimationFrame);

            }
            return vectors;
        } catch (error) {
            throw error
        }
    }

    static splitIntoChunks(text, chunkSize = 800, overlap = 100, minChunkSize = 50) {
        if (!text || typeof text !== "string") return [];

        text = text.trim();
        if (text.length < minChunkSize) return [];

        if (text.length <= chunkSize) {
            return [text];
        }

        const chunks = [];
        let start = 0;

        while (start < text.length) {
            let end = Math.min(start + chunkSize, text.length);

            if (end < text.length) {
                const spaceIndex = text.lastIndexOf(" ", end);
                if (spaceIndex > start + minChunkSize) {
                    end = spaceIndex;
                }
            }

            const chunk = text.slice(start, end).trim();
            if (chunk.length >= minChunkSize) {
                chunks.push(chunk);
            }

            let nextStart = end - overlap;

            if (nextStart <= start) {
                nextStart = end;
            }

            start = nextStart;
        }

        return chunks;
    }
}